import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from 'react-bootstrap';
import { RelatedScreen, Screen } from '../types';
import { apiService } from '../services/api';
import { expandColumns, getValueByPath } from '../utils/columnExpander';
import { getColumnDisplayValue } from '../utils/screenDisplayValue';
import { formatTimestampForDisplay, formatDateForDisplay } from '../utils/timestampUtils';
import { useFilters } from '../contexts';
import { resolvePickerRequestFilters } from '../utils/pickerFilters';

interface M2ORecordBadgesProps {
    /** The screen whose columns describe the record. */
    screen: Screen | RelatedScreen;
    recordId: string | number;
    /** The record itself when the form already holds it; it is shown as is and never requested. */
    record?: any;
    onClick?: () => void;
    isInvalid?: boolean;
    disabled?: boolean;
    /** Values and screen of the open form: the record is requested with the picker's own parameters. */
    formValues?: Record<string, any>;
    formScreen?: any;
}

const resolveDisplay = (value: any, col: any): string => {
    if (col.columnType === 'one-to-many') {
        return String(Array.isArray(value) ? value.length : (value ?? 0));
    }
    if (value === null || value === undefined || value === '') return '';
    if (col.columnType === 'boolean') {
        const bool = value === true || value === 1 || value === '1' || value === 'true';
        return bool ? 'ANO' : 'NIE';
    }
    if (col.columnType === 'timestamp') return formatTimestampForDisplay(value);
    if (col.columnType === 'date') return formatDateForDisplay(value);
    if (typeof value === 'object') return getColumnDisplayValue(value, col);
    return String(value);
};

export const M2ORecordBadges: React.FC<M2ORecordBadgesProps> = ({ screen, recordId, record, onClick, isInvalid, disabled, formValues, formScreen }) => {
    const { getFilter } = useFilters();
    const requestFilters = resolvePickerRequestFilters(screen, formScreen, formValues, getFilter);

    const { data: requested, isLoading, isError } = useQuery({
        queryKey: ['screenRecord', screen.id, recordId, JSON.stringify(requestFilters)],
        queryFn: async () => {
            const response = await apiService.getScreenRecord(screen.id, recordId, requestFilters, false);
            if (!response.success) {
                throw new Error(response.message || 'Nepodarilo sa načítať záznam');
            }
            const payload: any = response.data;
            return payload?.data ?? payload;
        },
        enabled: !record && !!screen.id && !!recordId,
        staleTime: 30_000,
    });
    const data = record ?? requested;

    const expanded = expandColumns((screen.columns || []).filter(c => !c.screenHidden));

    const clickable = !disabled && !!onClick;
    const classes = [
        'form-control',
        'd-flex',
        'flex-wrap',
        'align-items-center',
        'gap-2',
        isInvalid ? 'is-invalid' : '',
    ].filter(Boolean).join(' ');

    const style: React.CSSProperties = {
        minHeight: '38px',
        cursor: clickable ? 'pointer' : 'default',
        backgroundColor: disabled ? '#f5f5f5' : undefined,
    };

    const content = () => {
        if (!record && isLoading) return <Spinner animation="border" size="sm" />;
        if ((!record && isError) || !data) return <span className="text-muted small">Nepodarilo sa načítať</span>;

        const badges: React.ReactNode[] = [];
        for (const col of expanded) {
            const value = getValueByPath(data, col.dataPath);
            const display = resolveDisplay(value, col);
            if (display === '') continue;
            badges.push(
                <span key={col.id + col.dataPath.join('.')} className="badge bg-secondary-lt text-body">
                    <span className="text-muted">{col.displayName}:</span>
                    <span className="fw-bold">{display}</span>
                </span>
            );
        }

        if (badges.length === 0) return <span className="text-muted small">Žiadne dáta</span>;
        return badges;
    };

    return (
        <div
            className={classes}
            style={style}
            onClick={clickable ? onClick : undefined}
            role={clickable ? 'button' : undefined}
        >
            <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                {content()}
            </div>
            {clickable && (
                <button
                    type="button"
                    className="v5-assign ms-auto"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick!();
                    }}
                >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14"/>
                    </svg>
                    Zmeniť
                </button>
            )}
        </div>
    );
};
